import React from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface ProtectedRouteProps extends Omit<RouteProps, 'render'> {
  roles?: string[];
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  roles,
  children,
  ...rest
}) => {
  const { user, loading } = useAuth();

  // Only log in development mode
  if (process.env.NODE_ENV === 'development') {
    // Use a more concise log format
    console.log(`ProtectedRoute: ${roles ? roles.join(',') : 'any'} | User: ${user ? user.username : 'none'}`);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        // Only log in development mode and with minimal output
        const isDev = process.env.NODE_ENV === 'development';

        if (!user) {
          if (isDev) console.log(`ProtectedRoute: No user, redirecting to login from ${props.location.pathname}`);
          return (
            <Redirect
              to={{
                pathname: '/login',
                state: { from: props.location, message: 'Please log in to access this page' }
              }}
            />
          );
        }

        if (roles && !roles.some(role => role.toLowerCase() === user.role.toLowerCase())) {
          if (isDev) console.log(`ProtectedRoute: Role mismatch ${user.role} vs ${roles.join(',')}`);
          return (
            <Redirect
              to={{
                pathname: '/',
                state: {
                  from: props.location,
                  error: `Access denied: You need ${roles.join(' or ')} role to view this page`
                }
              }}
            />
          );
        }

        if (isDev) console.log(`ProtectedRoute: Access granted for ${props.location.pathname}`);
        return React.cloneElement(children as React.ReactElement, props);
      }}
    />
  );
};